'use strict';

// Provisional first-year planning assumptions, not learned NWAC history. The
// evidence and review contract are in docs/planning/budget-seasonal-model-20261012.md.
// April ... March. Profiles are independent of allocation and current activity.
const COST_CATEGORIES = ['tuition', 'support', 'course_start', 'other'];
const DEFAULT_CATEGORY_MIX = Object.freeze({ tuition: 0.30, support: 0.53, course_start: 0.04, other: 0.13 });
const CATEGORY_WEIGHTS = Object.freeze({
  tuition: Object.freeze([7, 5, 3, 2, 6, 40, 4, 2.5, 1.5, 22, 4, 3]),
  support: Object.freeze([9, 5, 4, 3, 4, 12, 11, 10, 9, 12, 11, 10]),
  course_start: Object.freeze([8, 6, 5, 4, 8, 32, 6, 4, 2, 18, 4, 3]),
  other: Object.freeze(Array(12).fill(1)),
});

function normalizeFundingCategory(value) {
  const key = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['tuition', 'tuition_fees', 'tuition_fee', 'fees', 'course_fees'].includes(key)) return 'tuition';
  if (['support', 'living', 'living_allowance', 'living_allowances', 'childcare', 'child_care', 'dependent_care'].includes(key)) return 'support';
  if (['course_start', 'books', 'books_materials', 'books_and_materials', 'materials', 'supplies', 'books_supplies'].includes(key)) return 'course_start';
  // Unknown/miscellaneous costs retain a rolling profile rather than silently
  // acquiring a tuition peak. Transportation can include one-off relocation.
  return 'other';
}

function normalizedWeights(weights) {
  if (!Array.isArray(weights) || weights.length !== 12 || weights.some(value => !Number.isFinite(value) || value < 0)) {
    throw new Error('A seasonal profile needs twelve non-negative weights.');
  }
  const total = weights.reduce((result, value) => result + value, 0);
  if (!(total > 0) || !Number.isFinite(total)) throw new Error('A seasonal profile needs twelve non-negative weights.');
  return weights.map(value => value / total);
}

function seasonalProfile(seasonality) {
  const verified = seasonality?.source === 'verified_history' && Number(seasonality.completeYears) >= 2;
  const supplied = Boolean(seasonality?.weights || seasonality?.profiles);
  const profiles = Object.fromEntries(COST_CATEGORIES.map(category => [category,
    normalizedWeights(seasonality?.weights || seasonality?.profiles?.[category] || CATEGORY_WEIGHTS[category])]));
  // A partially supplied profile cannot confer historical validation on the
  // default assumptions used for the remaining categories.
  const allSupplied = Boolean(seasonality?.weights) || COST_CATEGORIES.every(category => seasonality?.profiles?.[category]);
  const validated = verified && allSupplied;
  return { profiles, validated, source: validated ? 'verified_history' : supplied ? 'seasonal_assumption' : 'provisional_cost_type' };
}

function categoryMix(value = DEFAULT_CATEGORY_MIX) {
  const entries = COST_CATEGORIES.map(category => Number(value[category] ?? 0));
  if (entries.some(entry => !Number.isFinite(entry) || entry < 0)) throw new Error('Invalid funding category mix.');
  const total = entries.reduce((result, entry) => result + entry, 0);
  if (!(total > 0) || !Number.isFinite(total)) throw new Error('Invalid funding category mix.');
  return entries.map(entry => entry / total);
}

module.exports = { COST_CATEGORIES, DEFAULT_CATEGORY_MIX, CATEGORY_WEIGHTS, normalizeFundingCategory, seasonalProfile, categoryMix };
